/**
 * ReportDateRangeSelector Component
 *
 * Date range picker for report generation.
 * Defaults to current month start through today.
 */

import React from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';

interface ReportDateRangeSelectorProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  disabled?: boolean;
}

/**
 * Get default start date (first day of current month)
 */
export function getDefaultStartDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * Get default end date (today)
 */
export function getDefaultEndDate(): string {
  const now = new Date();
  return now.toISOString().split('T')[0];
}

export const ReportDateRangeSelector: React.FC<ReportDateRangeSelectorProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  disabled = false,
}) => {
  // Validate that end date is not before start date
  const isValidRange = !startDate || !endDate || startDate <= endDate;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Start Date */}
        <div>
          <label
            htmlFor="report-start-date"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Start Date
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="date"
              id="report-start-date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              disabled={disabled}
              className={`
                block w-full pl-10 pr-3 py-2 text-sm border rounded-md shadow-sm
                focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
                ${disabled
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  : 'bg-white text-gray-900'
                }
                ${!isValidRange ? 'border-red-300' : 'border-gray-300'}
              `}
            />
          </div>
        </div>

        {/* End Date */}
        <div>
          <label
            htmlFor="report-end-date"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            End Date
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="date"
              id="report-end-date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              disabled={disabled}
              min={startDate} // Prevent selecting date before start
              className={`
                block w-full pl-10 pr-3 py-2 text-sm border rounded-md shadow-sm
                focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
                ${disabled
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  : 'bg-white text-gray-900'
                }
                ${!isValidRange ? 'border-red-300' : 'border-gray-300'}
              `}
            />
          </div>
        </div>
      </div>

      {/* Validation Error */}
      {!isValidRange && (
        <p className="text-sm text-red-600">
          End date must be on or after start date.
        </p>
      )}
    </div>
  );
};
